import React, { useRef } from 'react';
import Button from '../common/Button';
import { CameraIcon } from '../icons/Icons';
import { theme } from '../../config/theme';
import { formatFileSize } from '../../utils/helpers';

const ProductImageUpload = ({ 
  images = [], 
  existingImages = [],
  onImageAdd, 
  onImageRemove,
  onExistingImageRemove,
  maxImages = 5,
  maxSize = 5 * 1024 * 1024,
}) => {
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const totalImages = existingImages.length + images.length + files.length;

    if (totalImages > maxImages) {
      alert(`Maximum ${maxImages} images allowed`);
      return;
    }

    const invalidFiles = files.filter(file => file.size > maxSize);
    if (invalidFiles.length > 0) {
      alert(`Some files exceed ${formatFileSize(maxSize)} limit`);
      return;
    }

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const invalidTypes = files.filter(file => !validTypes.includes(file.type));
    if (invalidTypes.length > 0) {
      alert('Only JPG, PNG, and WEBP images are allowed');
      return;
    }

    onImageAdd(files);
    e.target.value = '';
  };

  const gridStyles = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
    gap: theme.spacing.md,
    marginTop: theme.spacing.md,
  };

  const imageContainerStyles = {
    position: 'relative',
    width: '100%',
    paddingBottom: '100%',
    borderRadius: theme.borderRadius.md,
    overflow: 'hidden',
    border: `2px solid ${theme.colors.gray[200]}`,
  };

  const imageStyles = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  };

  const removeButtonStyles = {
    position: 'absolute',
    top: theme.spacing.xs,
    right: theme.spacing.xs,
    backgroundColor: theme.colors.error,
    color: theme.colors.white,
    border: 'none',
    borderRadius: theme.borderRadius.full,
    width: '24px',
    height: '24px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: theme.typography.fontSize.sm,
    fontWeight: theme.typography.fontWeight.bold,
  };

  const uploadBoxStyles = {
    border: `2px dashed ${theme.colors.gray[300]}`,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.xl,
    textAlign: 'center',
    cursor: 'pointer',
    transition: theme.transitions.base,
    backgroundColor: theme.colors.gray[50],
  };

  const iconWrapperStyles = {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing.sm,
    color: theme.colors.gray[400],
  };

  const totalImages = existingImages.length + images.length;
  const canAddMore = totalImages < maxImages;

  return (
    <div>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: theme.spacing.md,
      }}>
        <label style={{
          fontSize: theme.typography.fontSize.sm,
          fontWeight: theme.typography.fontWeight.medium,
        }}>
          Product Images
        </label>
        <span style={{ 
          fontSize: theme.typography.fontSize.xs, 
          color: theme.colors.text.secondary,
        }}>
          {totalImages}/{maxImages} images
        </span>
      </div>

      {canAddMore && (
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            multiple
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <div 
            style={uploadBoxStyles}
            onClick={() => fileInputRef.current?.click()}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = theme.colors.primary.main;
              e.currentTarget.style.backgroundColor = `${theme.colors.primary.main}10`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = theme.colors.gray[300];
              e.currentTarget.style.backgroundColor = theme.colors.gray[50];
            }}
          >
            <div style={iconWrapperStyles}>
              <CameraIcon size={48} color={theme.colors.gray[400]} />
            </div>
            <div style={{ 
              fontSize: theme.typography.fontSize.sm, 
              fontWeight: theme.typography.fontWeight.medium,
              marginBottom: theme.spacing.xs,
            }}>
              Click to upload images
            </div>
            <div style={{ 
              fontSize: theme.typography.fontSize.xs, 
              color: theme.colors.text.secondary,
            }}>
              JPG, PNG, WEBP • Max {formatFileSize(maxSize)} each
            </div>
          </div>
        </div>
      )}

      {(existingImages.length > 0 || images.length > 0) && (
        <div style={gridStyles}>
          {existingImages.map((image, index) => (
            <div key={`existing-${index}`} style={imageContainerStyles}>
              <img src={image} alt={`Existing ${index + 1}`} style={imageStyles} />
              <button
                style={removeButtonStyles}
                onClick={() => onExistingImageRemove(index)}
                title="Remove image"
              >
                ×
              </button>
            </div>
          ))}
          {images.map((image, index) => (
            <div key={`new-${index}`} style={imageContainerStyles}>
              <img 
                src={URL.createObjectURL(image)} 
                alt={`New ${index + 1}`} 
                style={imageStyles} 
              />
              <button
                style={removeButtonStyles}
                onClick={() => onImageRemove(index)}
                title="Remove image"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductImageUpload;